import type {
    FC,
    InputHTMLAttributes,
    ReactNode,
    SelectHTMLAttributes,
    TextareaHTMLAttributes
} from 'react'

import { useState } from 'react'
import { cn } from '@/lib/utils'

import {
    ChevronDown,
    CircleAlert,
    CircleCheck,
    Eye,
    EyeOff,
    Info
} from 'lucide-react'

export const fieldClass = (invalid?: boolean): string => {
    return cn(
        'block w-full rounded-xl border bg-white px-4 text-base text-ink shadow-xs outline-none transition-[border-color,box-shadow] placeholder:text-slate-400 focus:ring-4 disabled:bg-muted sm:text-[15px]',
        invalid
            ? 'border-rose-500 ring-1 ring-rose-500 focus:ring-rose-500/15'
            : 'border-input focus:border-brand-500 focus:ring-brand-500/15'
    )
}

const describedBy = (id: string, error?: string, hint?: ReactNode): string | undefined => {
    if (error) return `${id}-error`
    if (hint) return `${id}-hint`

    return undefined
}

interface FieldShellProps {
    id: string
    label: string
    hint?: ReactNode
    error?: string
    className?: string
    children: ReactNode
}

export const FieldShell: FC<FieldShellProps> = ({
    id,
    label,
    hint,
    error,
    className,
    children
}): ReactNode => {
    return (
        <div className={className}>
            <label htmlFor={id} className='block text-sm font-semibold text-ink'>
                {label}
            </label>

            <div className='mt-2'>
                {children}
            </div>

            {error ? (
                <p id={`${id}-error`} className='mt-2 flex items-start gap-1.5 text-sm text-rose-700'>
                    <CircleAlert className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                    {error}
                </p>
            ) : hint ? (
                <p id={`${id}-hint`} className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                    {hint}
                </p>
            ) : null}
        </div>
    )
}

type FieldBaseProps = {
    id: string
    label: string
    hint?: ReactNode
    error?: string
    className?: string
}

type TextFieldProps = FieldBaseProps & Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className'>

export const TextField: FC<TextFieldProps> = ({
    id,
    label,
    hint,
    error,
    className,
    ...inputProps
}): ReactNode => {
    return (
        <FieldShell id={id} label={label} hint={hint} error={error} className={className}>
            <input
                id={id}
                aria-invalid={Boolean(error)}
                aria-describedby={describedBy(id, error, hint)}
                className={cn(fieldClass(Boolean(error)), 'h-12')}
                {...inputProps}
            />
        </FieldShell>
    )
}

type PasswordFieldProps = FieldBaseProps & Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className' | 'type'>

export const PasswordField: FC<PasswordFieldProps> = ({
    id,
    label,
    hint,
    error,
    className,
    ...inputProps
}): ReactNode => {
    const [visible, setVisible] = useState(false)

    return (
        <FieldShell id={id} label={label} hint={hint} error={error} className={className}>
            <div className='relative'>
                <input
                    id={id}
                    type={visible ? 'text' : 'password'}
                    aria-invalid={Boolean(error)}
                    aria-describedby={describedBy(id, error, hint)}
                    className={cn(fieldClass(Boolean(error)), 'h-12 pr-12')}
                    {...inputProps}
                />

                <button
                    type='button'
                    onClick={() => setVisible(value => !value)}
                    aria-pressed={visible}
                    className='absolute top-1/2 right-1.5 inline-flex size-9 -translate-y-1/2 items-center justify-center rounded-lg text-slate-500 transition-colors outline-none hover:bg-muted hover:text-ink focus-visible:ring-4 focus-visible:ring-ring/30'
                >
                    {visible ? <EyeOff className='size-5' aria-hidden='true' /> : <Eye className='size-5' aria-hidden='true' />}

                    <span className='sr-only'>
                        {visible ? 'Sembunyikan kata sandi' : 'Tampilkan kata sandi'}
                    </span>
                </button>
            </div>
        </FieldShell>
    )
}

type SelectFieldProps = FieldBaseProps & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'className'> & {
    options: Array<{ value: string, label: string }>
    placeholder: string
}

export const SelectField: FC<SelectFieldProps> = ({
    id,
    label,
    hint,
    error,
    className,
    options,
    placeholder,
    value,
    ...selectProps
}): ReactNode => {
    return (
        <FieldShell id={id} label={label} hint={hint} error={error} className={className}>
            <div className='relative'>
                <select
                    id={id}
                    value={value}
                    aria-invalid={Boolean(error)}
                    aria-describedby={describedBy(id, error, hint)}
                    className={cn(fieldClass(Boolean(error)), 'h-12 appearance-none pr-11', !value && 'text-slate-400')}
                    {...selectProps}
                >
                    <option value='' disabled>
                        {placeholder}
                    </option>

                    {options.map(option => (
                        <option key={option.value} value={option.value} className='text-ink'>
                            {option.label}
                        </option>
                    ))}
                </select>

                <ChevronDown className='pointer-events-none absolute top-1/2 right-4 size-5 -translate-y-1/2 text-slate-500' aria-hidden='true' />
            </div>
        </FieldShell>
    )
}

type TextareaFieldProps = FieldBaseProps & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'className'>

export const TextareaField: FC<TextareaFieldProps> = ({
    id,
    label,
    hint,
    error,
    className,
    ...textareaProps
}): ReactNode => {
    return (
        <FieldShell id={id} label={label} hint={hint} error={error} className={className}>
            <textarea
                id={id}
                aria-invalid={Boolean(error)}
                aria-describedby={describedBy(id, error, hint)}
                className={cn(fieldClass(Boolean(error)), 'min-h-28 resize-none py-3 leading-relaxed')}
                {...textareaProps}
            />
        </FieldShell>
    )
}

const alertTones = {
    danger: {
        box: 'bg-rose-50 ring-rose-100',
        icon: CircleAlert,
        iconClass: 'text-rose-600',
        title: 'text-rose-700'
    },
    info: {
        box: 'bg-sky-50 ring-sky-100',
        icon: Info,
        iconClass: 'text-sky-700',
        title: 'text-sky-800'
    },
    success: {
        box: 'bg-emerald-50 ring-emerald-100',
        icon: CircleCheck,
        iconClass: 'text-emerald-600',
        title: 'text-emerald-800'
    }
}

interface FormAlertProps {
    tone?: keyof typeof alertTones
    title: string
    children?: ReactNode
    className?: string
}

export const FormAlert: FC<FormAlertProps> = ({
    tone = 'danger',
    title,
    children,
    className
}): ReactNode => {
    const style = alertTones[tone]
    const Icon = style.icon

    return (
        <div
            role={tone === 'danger' ? 'alert' : undefined}
            className={cn('flex gap-3 rounded-2xl p-4 ring-1 ring-inset', style.box, className)}
        >
            <Icon className={cn('mt-0.5 size-5 shrink-0', style.iconClass)} aria-hidden='true' />

            <div className='min-w-0'>
                <p className={cn('text-sm font-bold', style.title)}>
                    {title}
                </p>

                {children && (
                    <div className='mt-1 text-sm leading-relaxed text-ink-soft'>
                        {children}
                    </div>
                )}
            </div>
        </div>
    )
}

interface AuthHeadingProps {
    title: string
    description?: ReactNode
}

export const AuthHeading: FC<AuthHeadingProps> = ({ title, description }): ReactNode => {
    return (
        <div>
            <h1 className='text-[1.75rem] leading-tight font-extrabold tracking-tight text-ink sm:text-3xl'>
                {title}
            </h1>

            {description && (
                <p className='mt-2 text-[15px] leading-relaxed text-muted-foreground'>
                    {description}
                </p>
            )}
        </div>
    )
}

export const AuthDivider: FC = (): ReactNode => {
    return <hr className='my-6 border-border' />
}
